import { createContext, useContext, useState, useEffect } from "react";
import { apiClient } from "../api/ApiClient";
import { executeJwtAuthenticationService, fetchUserDetailsService } from "../api/AuthenticationApiService";
import Loading from "../../userDashboard/auth/loaders/Loading";

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export default function AuthProvider({ children }) {
    const [isAuthenticated, setAuthenticated] = useState(false);
    const [username, setUsername] = useState(null);
    const [token, setToken] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSuperLogin, setIsSuperLogin] = useState(false);
    const [originalUserId, setOriginalUserId] = useState(null);

    const updateUserDetails = (newDetails) => {
        setUserDetails(newDetails);
    };

    const exitSuperLogin = async () => {
        const adminToken = localStorage.getItem('adminToken');
        if (adminToken) {
            // Clear current session
            localStorage.removeItem('token');
            localStorage.removeItem('isSuperLogin');
            localStorage.removeItem('originalUserId');
            
            // Restore admin session
            setToken(adminToken);
            setIsSuperLogin(false);
            localStorage.setItem('token', adminToken);
            localStorage.removeItem('adminToken');
            
            // Update API client
            apiClient.interceptors.request.use((config) => {
                config.headers.Authorization = `Bearer ${adminToken}`;
                return config;
            });
            
            // Redirect to admin dashboard
            window.location.href = '/main/admindashboard/main';
        }
    };

    const login = async (email, password) => {
        try {
            const response = await executeJwtAuthenticationService(email, password);
    
            if (response.status === 200) {
                const jwtToken = 'Bearer ' + response.data.access_token;

                // Clear any existing super login state
                localStorage.removeItem('isSuperLogin');
                localStorage.removeItem('originalUserId');
                localStorage.removeItem('adminToken');
    
                setAuthenticated(true);
                setUsername(email);
                setToken(jwtToken);
                setIsSuperLogin(false);
    
                localStorage.setItem('token', jwtToken);
                localStorage.setItem('email', email);
    
                apiClient.interceptors.request.use((config) => {
                    config.headers.Authorization = jwtToken;
                    return config;
                });

                const userDetailsResponse = await fetchUserDetailsService(email);
                setUserDetails(userDetailsResponse.data);
    
                return { success: true };
            }
        } catch (error) {
            console.error('Login error:', error.response || error);

            if (error.response) {
                switch (error.response.status) { 
                    case 401:
                        return { success: false, message: 'Invalid email or password' };
                    case 403:
                        return { success: false, message: 'Reload Session for authentication' };
                    case 400:
                        return { success: false, message: 'Reload Session for authentication' };
                    case 404:
                        return { success: false, message: 'User not found. Please check your email or sign up.' };
                    default:
                        return { success: false, message: 'Reload Session for authentication' };
                }
            }
            return { success: false, message: 'Reload Session for authentication!' };
        }
    };


    const handleSuperLogin = (superLoginData) => {
        const { access_token, userDetails: targetUserDetails } = superLoginData;
        
        // Store current admin token
        const currentToken = localStorage.getItem('token');
        localStorage.setItem('adminToken', currentToken);
        
        // Set up super login session
        const superLoginToken = `Bearer ${access_token}`;
        localStorage.setItem('token', superLoginToken);
        localStorage.setItem('isSuperLogin', 'true');
        localStorage.setItem('originalUserId', targetUserDetails.id);
        
        setToken(superLoginToken);
        setIsSuperLogin(true);
        setUserDetails(targetUserDetails);
        setOriginalUserId(targetUserDetails.id);
        
        // Update API client
        apiClient.interceptors.request.use((config) => {
            config.headers.Authorization = superLoginToken;
            return config;
        });
    };


     const logout = () => {
        setAuthenticated(false);
        setToken(null);
        setUsername(null);
        setUserDetails(null);
        setIsSuperLogin(false);
        setOriginalUserId(null);
        
        // Clear all stored tokens and state
        localStorage.removeItem('token');
        localStorage.removeItem('email');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('isSuperLogin');
        localStorage.removeItem('originalUserId');
    };

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const storedToken = localStorage.getItem('token');
                const storedUsername = localStorage.getItem('email');
                const storedSuperLogin = localStorage.getItem('isSuperLogin') === 'true';
                const storedOriginalUserId = localStorage.getItem('originalUserId');

                if (storedToken && storedUsername) {
                    setAuthenticated(true);
                    setUsername(storedUsername);
                    setToken(storedToken);
                    setIsSuperLogin(storedSuperLogin);
                    setOriginalUserId(storedOriginalUserId);

                    apiClient.interceptors.request.use((config) => {
                        config.headers.Authorization = storedToken;
                        return config;
                    });

                    const response = await fetchUserDetailsService(storedUsername);
                    setUserDetails(response.data);
                }

            } catch (error) {
                console.error('Error initializing auth:', error);
                logout();
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();
    }, []);

    if (isLoading) {
        return <Loading />;
    }

     return (
        <AuthContext.Provider value={{
            isAuthenticated,
            login,
            logout,
            username,
            token,
            userDetails,
            updateUserDetails,
            isLoading,
            isSuperLogin,
            exitSuperLogin,
            handleSuperLogin,
            originalUserId
        }}>
            {children}
        </AuthContext.Provider>
    );
}
